import { useMsal } from "@azure/msal-react";
import {
    Checkbox,
    DefaultButton,
    IColumn,
    IPanelProps,
    IRenderFunction,
    Panel,
    PanelType, PrimaryButton,
    Separator, Spinner, Stack,
    Text
} from "@fluentui/react";
import { RootState } from "app/store";
import { DefaultGridConfig } from "components/controls/CustomDetailList/CustomDetailList";
import { IGridItem } from "components/controls/CustomDetailList/IGridItem";
import { saveAs } from 'file-saver';
import { emitCustomEvent, fadeIn, panelButtonsStyles } from "global/Application";
import { setProcessing } from "global/Reducer/statusSlice";
import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDataSource } from "../../../global/DataSources/AppDataSource";
import { MsalTokenSource } from "../../../global/DataSources/MsalTokenSource";
import * as FAM from "../../../model/FlexibleAttributeModel";
import CustomPanel from "../CheckList/CheckListItem/CustomPanel";

interface IProps {
    isOpen: boolean;

    // cancellation of transaction/partial actions
    onCancel?: () => void;

    // dialog closure request from parent component
    onDismiss: () => void

}


interface IItem extends IGridItem<FAM.IPlan> {
}

const PlanComparisonReport = (props: IProps) => {

    const [loading, setLoading] = useState<boolean>(false);
    const { instance } = useMsal();

    const tokenSource = new MsalTokenSource(instance);
    const dataSource = new AppDataSource(tokenSource);

    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const state = useSelector((state: RootState) => state);

    const data = useMemo(() => {

        const columns: IColumn[] = [];
        const items = [] as IItem[];

        const data = {
            items, columns,
        }

        if (!props.isOpen)
            return {
                items,
                columns,
            };

        columns.push({
            key: 'plan',
            fieldName: 'plan',
            name: 'Plan',
            minWidth: 250,
            isResizable: true,
        });


        state.data.json.plans.forEach((plan, i) => {
            items.push({
                key: plan.id,
                _index: i,
                plan: plan.name,
                data: plan,
            });
        });

        return data;

    }, [props.isOpen]);

    const storageId = `benefitsummaryreport.plan.grid`;

    const scopeGridConfig = () => {
        const u = state.status.gridConfigs[storageId];
        if (u)
            return u;

        const t = DefaultGridConfig();
        t.pageSize = -1;
        // t.sort = [{ column: { key: 'attributeSetName' }, isSortedDescending: false }];

        return t;
    }

    const dispatch = useDispatch();

    // const selection = useMemo(() => {
    //     const selection = new Selection({
    //         onSelectionChanged: () => {

    //         },
    //         selectionMode: SelectionMode.multiple,
    //         items: data.items as any,
    //     });
    //     selection.setIndexSelected(1, true, true);
    //     // return selection;
    // }, [data]);

    const [plans, setPlans] = useState<FAM.IPlan[]>([]);

    const onRenderBody: IRenderFunction<IPanelProps> = (bodyProps, defaultRender) => {
        if (!props.isOpen)
            return <></>;

        return (
            <div className={fadeIn}>
                <Separator />
                <Text variant="small">
                    🚧 Under Construction 🚧
                </Text>
                <CustomPanel
                    title="Settings"
                    canCollapse={true}
                >
                    <Stack tokens={{ childrenGap: '15px' }}>
                        <Checkbox disabled={true} label="Show only changes" />
                        <Checkbox disabled={true} label="Single Sheet with segment breaks" />
                        <Checkbox disabled={true} label="Benefits" />
                        <Checkbox disabled={true} label="Benefits - Nonstandard" />
                        <Checkbox disabled={true} label="Benefits - Nonstandard, skip the ones that didn't match on id/name" />
                        <Checkbox disabled={true} label="Benefits - AttributeSets" />
                        <Checkbox disabled={true} label="Fees And Premium" />
                        <Checkbox disabled={true} label="Group Structure" />
                        <Checkbox disabled={true} label="Ignore fields/rows that are expected to have a change (e.g. name)" />
                        <Checkbox disabled={true} label="Color code variations" />
                        <Checkbox disabled={true} label="High Level and Properties Only" />
                        <Checkbox disabled={true} label="Exclude blanks from comparison" />
                    </Stack>
                </CustomPanel >
            </div>
        )
    }

    const onRenderFooterContent: IRenderFunction<IPanelProps> = (footerProps, defaultRender) => {
        return <div>
            <PrimaryButton text={loading ? 'Generating...' : 'Continue'}
                styles={panelButtonsStyles}
                disabled={loading}
                onClick={async () => {
                    try {
                        dispatch(setProcessing(true));
                        const excelExtractImport = await import("global/ExcelExtract")
                        const { PlanComparisonReportGeneration } = excelExtractImport;
                        const result = await PlanComparisonReportGeneration(state, dataSource)
                        const fileName = `PlanComparisonReport-${new Date().getTime()}.xlsx`;
                        saveAs(result.file, fileName);
                    } catch (error) {
                        emitCustomEvent({
                            eventType: 'globalException',
                            details: error
                        });
                    } finally {
                        dispatch(setProcessing(false));
                    }
                }}
            >
                {loading && <Spinner />}
            </PrimaryButton>
            <DefaultButton text='Cancel' onClick={() => {
                setErrorMessage(null);
                props.onCancel?.();
            }} />
        </div>
    }


    let headerText = "Plan Comparison Report";

    return <Panel
        isOpen={props.isOpen}
        onDismiss={props.onDismiss}
        type={PanelType.medium}
        headerText={headerText}
        onRenderFooterContent={onRenderFooterContent}
        isFooterAtBottom={true}
    >
        {onRenderBody()}
    </Panel>;
}

export default PlanComparisonReport;